import { useOverrideHooks } from '@override';
/* eslint-disable max-statements */
import { useNavigate } from 'react-router-dom';

import { Batch, BatchTypeEnum, DeclarationStatus } from '@e-origin/shared';

import { TableActionEnum } from '@components';
import { DeclarationsTabs } from '@enums';
import {
  useArchiveBatch,
  useCleanupArchivedBatch,
  useDeleteBatch,
  useDownloadOriginalFileFromCloud,
  useExportScreenshots,
  useGenerateBatchRiskAnalysisReport,
  useGenerateInvoice,
  useRunRiskAnalysisCalculations,
  useSendBatchRiskAnalysisReport,
  useSyncBatchWithCustoms,
} from '@hooks/batch';
import { useEnableSimulatorMode } from '@hooks/batch/simulator/useEnableSimulatorMode';
import { useSettings } from '@hooks/useSettings';
import { useBatchModalManager } from '../batchModalManager';

const getDeclarationListTabs = (statistics) => [
  {
    field: 'analysis',
    tab: DeclarationStatus.ANALYSIS,
    count: statistics.analysis,
  },
  {
    field: 'beGate',
    tab: DeclarationStatus.BEGATE_SENT,
    count: statistics.beGateSent + statistics.beGateReleased + statistics.beGateNotReleased,
  },
  {
    field: 'notSent',
    tab: DeclarationStatus.NOT_SENT,
    count: statistics.notSent,
  },
  {
    field: 'sent',
    tab: DeclarationStatus.SENT,
    count: statistics.sent,
  },
  {
    field: 'accepted',
    tab: DeclarationStatus.ACCEPTED,
    count: statistics.accepted,
  },
  {
    field: 'rejected',
    tab: DeclarationStatus.REJECTED,
    count: statistics.rejected,
  },
  {
    field: 'inControl',
    tab: DeclarationStatus.IN_CONTROL,
    count: statistics.inControl,
  },
  {
    field: 'notReleased',
    tab: DeclarationStatus.NOT_RELEASED,
    count: statistics.notReleased,
  },
  {
    field: 'released',
    tab: DeclarationStatus.RELEASED,
    count: statistics.released,
  },
  {
    field: 'invalidated',
    tab: DeclarationStatus.INVALIDATED,
    count: statistics.invalidated,
  },
  {
    field: 'exported',
    tab: DeclarationsTabs.EXPORTED,
    count: statistics.exported,
  },
];

export const useBuildActionHandlers = ({ loadBatches, batchView }) => {
  const { data: settings } = useSettings();
  const batchModalManager = useBatchModalManager();
  const OverrideHooks = useOverrideHooks();

  const navigate = useNavigate();

  const generateBatchReportMutation = OverrideHooks.useGenerateBatchReport();
  const archiveBatch = useArchiveBatch();
  const cleanupArchivedBatch = useCleanupArchivedBatch();
  const deleteBatch = useDeleteBatch();
  const downloadOriginalFileFromCloud = useDownloadOriginalFileFromCloud();
  const exportScreenshots = useExportScreenshots();
  const generateBatchRiskAnalysisReport = useGenerateBatchRiskAnalysisReport();
  const generateCustomerReport = useSendBatchRiskAnalysisReport();
  const generateInvoice = useGenerateInvoice();
  const runRiskAnalysisCalculations = useRunRiskAnalysisCalculations();
  const syncBatchWithCustoms = useSyncBatchWithCustoms();
  const sendBatchRiskAnalysisReport = useSendBatchRiskAnalysisReport();
  const enableSimulatorMode = useEnableSimulatorMode();

  const showFailedModalDetails = (batch: Batch) => {
    batchModalManager.openFailedDetails({ batch });
  };

  const showDeleteBatch = (batch: Batch) => {
    batchModalManager.openDeleteBatch({ batch, onConfirm: () => handleBatchDelete(batch) });
  };
  const showArchiveModal = (batch: Batch, isUnarchiveFlag = false) => {
    batchModalManager.openArchiveBatch({
      onConfirm: () => handleBatchArchive(batch, isUnarchiveFlag),
      isUnarchive: isUnarchiveFlag,
    });
  };

  const handleBatchDelete = (batch: Batch) => {
    deleteBatch.mutate({
      batchId: batch._id,
      softDelete: [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView),
    });
  };

  const handleBatchArchive = (batch: Batch, isUnarchive = false) => {
    archiveBatch.mutate({ batchId: batch._id, isUnarchive });
  };

  const openUpdateBatch = (batch: Batch) => {
    batchModalManager.openUpdateBatch({
      batch,
      batchType: batchView,
      afterCreate: () => {
        loadBatches();
      },
    });
  };

  const openGenerateBatchInvoiceModal = async (batch: Batch) => {
    batchModalManager.openGenerateInvoice({
      batch: batch,
      onConfirm: () => generateInvoice.mutate({ batchId: batch._id, fileName: batch.filename }),
    });
  };

  const executeArchiveCleanup = async (batchId: string) => {
    cleanupArchivedBatch.mutate({ batchId });
    loadBatches();
  };

  const handleTableAction = (actionType: string, row: Batch) => {
    switch (actionType) {
      case TableActionEnum.View: {
        if (row.status.value === 'failed') {
          showFailedModalDetails(row);
        } else {
          const declarationListTabs = getDeclarationListTabs(row.statistics);
          declarationListTabs.sort((a, b) => b.count - a.count);

          // if the highest count is 'sent', check if the sent ones my be in another status (like rejected or accepted) to go there instead
          const tab = declarationListTabs[0].field === 'sent' && declarationListTabs[1].count > 0 ? declarationListTabs[1] : declarationListTabs[0];

          navigate({
            pathname: '/declarations',
            // if no statistics count, it means that they are still sending so we go to that tab
            search: `?batchId=${row._id}&tab=${!tab.count ? 'SENDING' : tab.tab}&batchName=${row.name}`,
          });
        }
        return;
      }
      case TableActionEnum.GenerateCustomsReport: {
        generateBatchReportMutation.mutate({ batchId: row._id });
        return;
      }
      case TableActionEnum.GenerateRiskAnalysisReport: {
        generateBatchRiskAnalysisReport.mutate({ batchId: row._id, fileName: row.filename });
        return;
      }
      case TableActionEnum.GenerateCustomerReport: {
        generateCustomerReport.mutate({ batchId: row._id, transitionToInValidate: false });
        return;
      }
      case TableActionEnum.GenerateInvoice: {
        if (row.cloudBucketInvoiceUrl) {
          generateInvoice.mutate({ batchId: row._id, fileName: row.filename });
        } else {
          openGenerateBatchInvoiceModal(row);
        }
        return;
      }
      case TableActionEnum.ExportScreenshots: {
        exportScreenshots.mutate({ batchId: row._id });
        return;
      }
      case TableActionEnum.ExecuteRiskAnalysis: {
        batchModalManager.openRiskAnalysis({
          batch: row,
        });
        return;
      }
      case TableActionEnum.SendRiskAnalysisReport: {
        sendBatchRiskAnalysisReport.mutate({ batchId: row._id, transitionToInValidate: true });
        return;
      }
      case TableActionEnum.Finalize: {
        const openFinalize = settings?.enableNewFinalizeBatch ? batchModalManager.openFinalizeBatch : batchModalManager.openFinalizeBatchLegacy;
        openFinalize({
          batch: row,
          afterFinalize: () => void 0,
        });
        return;
      }
      case TableActionEnum.Delete: {
        showDeleteBatch(row);
        return;
      }
      case TableActionEnum.Archive: {
        showArchiveModal(row);
        return;
      }
      case TableActionEnum.Update: {
        openUpdateBatch(row);
        return;
      }
      case TableActionEnum.DownloadFile: {
        if (row.virtuals.hasFileInCloud) {
          downloadOriginalFileFromCloud.mutate({ batchId: row._id, fileName: row.filename });
        }
        return;
      }
      case TableActionEnum.CustomsSync: {
        syncBatchWithCustoms.mutate({ batch: row });
        return;
      }
      case TableActionEnum.RiskAnalysisCalculations: {
        if (settings.isForwarder) {
          batchModalManager.openRunRiskAnalysisCalculations({ batch: row });
          return;
        }
        runRiskAnalysisCalculations.mutate({ batchId: row._id });
        return;
      }
      case TableActionEnum.Send: {
        batchModalManager.openSendBatch({ batchId: row._id });
        return;
      }
      case TableActionEnum.GenerateProformaB2BInvoice: {
        batchModalManager.openGenerateProformaB2BInvoice({ batch: row });
        return;
      }
      case TableActionEnum.BatchAudit: {
        batchModalManager.openBatchAudit({ batch: row });
        return;
      }
      case TableActionEnum.EnableSimulator: {
        enableSimulatorMode.mutate({ params: { batchId: row._id } });
        return;
      }
      case TableActionEnum.SimulateMessages: {
        batchModalManager.openSimulatorMessages({ batch: row });
        return;
      }
      default: {
        return;
      }
    }
  };

  const handleArchivedTableAction = (actionType: string, row: Batch) => {
    switch (actionType) {
      case TableActionEnum.View: {
        navigate(`/declarations?batchId=${row._id}&archived=true`);
        return;
      }
      case TableActionEnum.GenerateCustomsReport: {
        generateBatchReportMutation.mutate({ batchId: row._id });
        return;
      }
      case TableActionEnum.GenerateRiskAnalysisReport: {
        generateBatchRiskAnalysisReport.mutate({ batchId: row._id, fileName: row.filename });
        return;
      }
      case TableActionEnum.Archive: {
        showArchiveModal(row, true);
        return;
      }
      case TableActionEnum.Update: {
        openUpdateBatch(row);
        return;
      }
      case TableActionEnum.DownloadFile: {
        if (row.virtuals.hasFileInCloud) {
          downloadOriginalFileFromCloud.mutate({ batchId: row._id, fileName: row.filename });
        }
        return;
      }
      case TableActionEnum.GenerateInvoice: {
        if (row.cloudBucketInvoiceUrl) {
          generateInvoice.mutate({ batchId: row._id, fileName: row.filename });
        } else {
          openGenerateBatchInvoiceModal(row);
        }
        return;
      }
      case TableActionEnum.Cleanup: {
        executeArchiveCleanup(row._id);
        return;
      }
      default: {
        showDeleteBatch(row);
      }
    }
  };

  return {
    handleTableAction,
    handleArchivedTableAction,
  };
};
